import React, { useEffect, Component, ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { stopAllAudioAndTTS } from './utils/speechQueueManager';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AppLayout } from './layouts/AppLayout';
import { AuthPage } from './pages/Auth';
import { ResetPasswordPage } from './pages/ResetPassword';
import { DashboardPage } from './pages/Dashboard';
import { StudentReportPage } from './pages/StudentReportPage';
import { AIMentorPage } from './pages/AIMentor';
import { CoursesPage } from './pages/Courses';
import { CourseDetailPage } from './pages/CourseDetail';
import { LessonPage } from './pages/Lesson';
import { LearningPathPage } from './pages/LearningPath';
import { CodeEditorPage } from './pages/CodeEditor';
import { QuizzesPage } from './pages/Quizzes';
import { ParentDashboardPage } from './pages/ParentDashboard';
import { ProjectsPage } from './pages/Projects';
import { ProjectWorkspacePage } from './pages/ProjectWorkspace';
import { CareerPage } from './pages/Career';
import { BadgesPage } from './pages/Badges';
import { LeaderboardPage } from './pages/Leaderboard';
import { CareerLeaguePage } from './pages/CareerLeague';
import { ProfilePage } from './pages/Profile';
import { SettingsPage } from './pages/Settings';
import { NotesPage } from './pages/Notes';
import { HackathonPage } from './pages/Hackathon';
import { PricingPage } from './pages/Pricing';
import { CertificatesPage } from './pages/Certificates';
import { CodeReviewPage } from './pages/CodeReview';
import { AdventureModePage } from './pages/AdventureMode';
import { AdminLayout } from './layouts/AdminLayout';
import { AdminOverviewPage } from './pages/admin/AdminOverviewPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AdminCoinShopPage } from './pages/admin/AdminCoinShopPage';
import { AdminPlansPage } from './pages/admin/AdminPlansPage';
import { AdminCoursesPage } from './pages/admin/AdminCoursesPage';
import { AdminStudentAnalyticsPage } from './pages/admin/AdminStudentAnalyticsPage';
import { AdminAiUsagePage } from './pages/admin/AdminAiUsagePage';
import { AdminRevenuePage } from './pages/admin/AdminRevenuePage';
import { AdminSchoolPage } from './pages/admin/AdminSchoolPage';
import { AdminMessagesPage } from './pages/admin/AdminMessagesPage';
import { LandingPage } from './pages/LandingPage';
import { LearningJourneyPage } from './pages/LearningJourney';
import { CoinShopPage } from './pages/CoinShop';
import { GameCenterPage } from './pages/GameCenter';
import { MemoryGamePage } from './pages/games/MemoryGame';
import { SnakeGamePage } from './pages/games/SnakeGame';
import { SudokuGamePage } from './pages/games/SudokuGame';
import { TicTacToeGamePage } from './pages/games/TicTacToeGame';
import { TetrisGamePage } from './pages/games/TetrisGame';

interface Props { children: ReactNode; }
interface State { hasError: boolean; error: Error | null; }

class AppErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false, error: null };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App Uncaught Error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#F7F4EC',
          color: '#10142A',
          fontFamily: 'sans-serif',
          padding: 24,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🚀</div>
          <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '1.8rem', margin: '0 0 12px 0' }}>
            Sayfa Yüklenirken Bir Güncelleme Algılandı
          </h2>
          <p style={{ color: '#6E6549', maxWidth: 480, margin: '0 0 24px 0', lineHeight: 1.6 }}>
            Uygulama son değişiklikler doğrultusunda güncellendi. Sayfayı yenileyerek hemen devam edebilirsiniz.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'linear-gradient(180deg, #17C79A, #0B8F70)',
              color: '#052E24',
              border: 'none',
              borderRadius: 100,
              padding: '12px 28px',
              fontWeight: 700,
              fontSize: '1rem',
              cursor: 'pointer',
              boxShadow: '0 6px 18px rgba(23,199,154,0.4)'
            }}
          >
            Sayfayı Yenile
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function LoadingScreen() {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#F7F4EC',
      color: '#10142A',
      fontFamily: 'sans-serif',
      gap: 16
    }}>
      <div style={{
        width: 44,
        height: 44,
        border: '4px solid rgba(23, 199, 154, 0.2)',
        borderTopColor: '#17C79A',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <span style={{ fontWeight: 700, fontSize: '0.98rem', color: '#10142A' }}>
        MinikCoder Akademi Yükleniyor...
      </span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function HomeRoute() {
  const { session, profile, isLoading, isRecovery } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  // Sadece gerçek şifre sıfırlama işlemleri reset-password sayfasına yönlendirilmeli.
  // Supabase'in e-posta doğrulama maili de access_token içerir;
  // onu reset akışı gibi yorumlamak yanlıştır.
  if (isRecovery || window.location.hash.includes('type=recovery') || window.location.search.includes('type=recovery')) {
    return <Navigate to="/reset-password" replace />;
  }

  if (!session) {
    return <LandingPage />;
  }

  const target =
    profile?.role === 'parent'
      ? '/parent'
      : profile?.role === 'admin'
      ? '/admin'
      : profile?.role === 'school'
      ? '/admin/school'
      : '/dashboard';
  return <Navigate to={target} replace />;
}

function NavigationAudioGuard() {
  const location = useLocation();

  // Sitede rota veya sayfa değiştiğinde önceki sayfadan kalan tüm ses/TTS ve akışları anında kes
  useEffect(() => {
    stopAllAudioAndTTS();
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  const initialize = useAuthStore((s) => s.initialize);

  useEffect(() => {
    const unsubscribe = initialize();
    return unsubscribe;
  }, [initialize]);

  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <NavigationAudioGuard />
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/callback" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route path="/dashboard/report" element={<StudentReportPage />} />
            <Route path="/ai-mentor" element={<AIMentorPage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/notes" element={<NotesPage />} />
            <Route path="/courses/:courseId" element={<CourseDetailPage />} />
            <Route path="/lessons/:lessonId" element={<LessonPage />} />
            <Route path="/learning-path" element={<LearningPathPage />} />
            <Route path="/code-editor" element={<CodeEditorPage />} />
            <Route path="/quizzes" element={<QuizzesPage />} />
            <Route
              path="/parent"
              element={
                <ProtectedRoute allowedRoles={['parent']}>
                  <ParentDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:projectId" element={<ProjectWorkspacePage />} />
            <Route path="/career" element={<CareerPage />} />
            <Route path="/badges" element={<BadgesPage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/career-league" element={<CareerLeaguePage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/learning-journey" element={<LearningJourneyPage />} />
            <Route path="/coin-shop" element={<CoinShopPage />} />
            <Route path="/hackathon" element={<HackathonPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/certificates" element={<CertificatesPage />} />
            <Route path="/code-review" element={<CodeReviewPage />} />
            <Route path="/adventure" element={<AdventureModePage />} />
            <Route path="/games" element={<GameCenterPage />} />
            <Route path="/games/memory" element={<MemoryGamePage />} />
            <Route path="/games/snake" element={<SnakeGamePage />} />
            <Route path="/games/sudoku" element={<SudokuGamePage />} />
            <Route path="/games/tic-tac-toe" element={<TicTacToeGamePage />} />
            <Route path="/games/tetris" element={<TetrisGamePage />} />
          </Route>

          <Route
            element={
              <ProtectedRoute allowedRoles={['admin', 'school']}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/admin" element={<AdminOverviewPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/coin-shop" element={<AdminCoinShopPage />} />
            <Route path="/admin/plans" element={<AdminPlansPage />} />
            <Route path="/admin/courses" element={<AdminCoursesPage />} />
            <Route path="/admin/student-analytics" element={<AdminStudentAnalyticsPage />} />
            <Route path="/admin/ai-usage" element={<AdminAiUsagePage />} />
            <Route path="/admin/revenue" element={<AdminRevenuePage />} />
            <Route path="/admin/school" element={<AdminSchoolPage />} />
            <Route path="/admin/messages" element={<AdminMessagesPage />} />
          </Route>

          <Route path="/" element={<HomeRoute />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

export default App;
